import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Vg2Shell } from "./Vg2Shell";
import {
  getPaCaseDetail, newOperationId,
  postPaCallSummary, postPaEntry,
} from "@/lib/vg2PaWorkspace";
import { useAuth } from "@/lib/AuthContext";
import PaCircleSection from "./PaCircleSection";
import DocumentSection from "./DocumentSection";
import PaAnalysesSection from "./PaAnalysesSection";

/**
 * PA Case detail — Phase 8B (§10, §11).
 * Sections ordered for a ~1 minute time-to-understand target.
 */
export default function PaWorkspaceCase() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const isPa = !!user && ["admin", "fpa", "pa", "standard"].includes(user.ruolo);
  const paId = user?.id;
  const [view, setView] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [drawer, setDrawer] = React.useState(null); // { kind }

  const load = React.useCallback(async () => {
    if (!isPa) { setView(null); return; }
    setError(null);
    try {
      const v = await getPaCaseDetail({ caseId });
      setView(v);
    } catch (e) { setError(e); }
  }, [isPa, caseId]);
  React.useEffect(() => { load(); }, [load]);

  const scrollTo = (target) => {
    const el = document.getElementById(`pa-section-${target}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    // Move focus for keyboard users (§63).
    el.setAttribute("tabindex", "-1");
    el.focus({ preventScroll: true });
  };

  if (authLoading) {
    return (
      <Vg2Shell>
        <main className="max-w-3xl mx-auto px-4 py-8">
          <p className="text-sm text-vg2-muted" data-testid="pa-case-auth-loading">
            Caricamento…
          </p>
        </main>
      </Vg2Shell>
    );
  }

  if (!isPa) {
    return (
      <Vg2Shell>
        <main className="max-w-3xl mx-auto px-4 py-8">
          <p className="text-sm text-vg2-ink mb-3"
             data-testid="pa-case-not-authorized">
            Questa area è riservata ai Property Advisor. Accedi con il tuo
            account Property Advisor per consultare il caso.
          </p>
          <button onClick={() => navigate("/property-advisor/login")}
                  className="px-4 py-2 border border-vg2-red text-vg2-red rounded hover:bg-vg2-red hover:text-white"
                  data-testid="pa-case-login">
            Accedi come Property Advisor
          </button>
        </main>
      </Vg2Shell>
    );
  }

  if (error) {
    return (
      <Vg2Shell>
        <main className="max-w-3xl mx-auto px-4 py-8">
          <p className="text-sm text-vg2-ink mb-3"
             data-testid="pa-case-error">
            {error.code === "PA_CONSENT_REVOKED"
              ? "Il proprietario non ha un consenso valido attivo per la condivisione di questo caso. Non puoi consultare i dati, i recapiti né i materiali condivisi."
              : error.code === "PA_ACCESS_INVALID"
                ? "Non hai accesso a questo caso."
                : error.status === 404
                  ? "Caso non trovato."
                  : (error.message || "Errore imprevisto.")}
          </p>
          <button onClick={() => navigate("/vg2/pa")}
                  className="px-4 py-2 border border-vg2-red text-vg2-red rounded hover:bg-vg2-red hover:text-white"
                  data-testid="pa-case-back">
            Torna al Workspace PA
          </button>
        </main>
      </Vg2Shell>
    );
  }

  if (!view) {
    return (
      <Vg2Shell>
        <main className="max-w-3xl mx-auto px-4 py-8">
          <p className="text-sm text-vg2-muted"
             data-testid="pa-case-loading">Caricamento…</p>
        </main>
      </Vg2Shell>
    );
  }

  return (
    <Vg2Shell>
      <main className="max-w-[900px] mx-auto px-4 py-8"
            data-testid="pa-case-root">
        <button onClick={() => navigate("/vg2/pa")}
                className="text-sm text-vg2-muted hover:text-vg2-ink underline mb-4"
                data-testid="pa-case-back">
          ← Torna al Workspace PA
        </button>

        <CaseHeader view={view} caseId={caseId} />
        <CallFocusSection view={view} onFocusClick={scrollTo} />
        <ObjectiveSection view={view} />
        <DecisionSection view={view} />
        <ActionPlanSection view={view} />
        <HandoffSection view={view} />
        <EntriesSection view={view}
                        onAdd={() => setDrawer({ kind: "entry" })} />
        <DocumentsSection view={view} />
        <PrerequisitesSection view={view} />
        <OpenPointsSection view={view} />
        <RolesSection view={view} />
        <PaCircleSection paId={paId} caseId={caseId} />
        <PaAnalysesSection caseId={caseId} />
        <DocumentSection variant="PA" paId={paId} caseId={caseId} />
        <CallSummarySection view={view}
                            onRecord={() => setDrawer({ kind: "call_summary" })} />
        <ReadinessSection view={view} />

        {drawer?.kind === "entry" && (
          <EntryDrawer view={view} paId={paId} caseId={caseId}
                       onClose={() => setDrawer(null)}
                       onSaved={async () => { setDrawer(null); await load(); }} />
        )}
        {drawer?.kind === "call_summary" && (
          <CallSummaryDrawer view={view} paId={paId} caseId={caseId}
                             onClose={() => setDrawer(null)}
                             onSaved={async () => { setDrawer(null); await load(); }} />
        )}
      </main>
    </Vg2Shell>
  );
}


// ---------- sections ------------------------------------------------------

function Section({ id, title, children, testid }) {
  return (
    <section id={`pa-section-${id}`} className="mb-6"
             data-testid={testid || `pa-case-section-${id}`}>
      <h2 className="font-display text-lg font-medium mb-3">{title}</h2>
      {children}
    </section>
  );
}


const STATUS_LABELS_IT = {
  NEW: "Nuovo",
  TO_REVIEW: "Da rivedere",
  READY_FOR_CALL: "Pronto per la call",
  CALL_COMPLETED: "Call svolta",
  REASSESSMENT_REQUIRED: "Da riesaminare",
  READY_FOR_EXECUTION: "Pronto per esecuzione",
  CLOSED: "Chiuso",
};


function CaseHeader({ view, caseId }) {
  const ref = (caseId || "").replace(/^case_/, "").slice(0, 8).toUpperCase();
  return (
    <header className="mb-6" data-testid="pa-case-header">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-red mb-1"
         title={caseId} data-testid="pa-case-ref">
        Caso {ref ? `· ${ref}` : ""}
      </p>
      <h1 className="font-display text-2xl md:text-3xl font-medium mb-2"
          data-testid="pa-case-property">
        {view.property_display_label}
      </h1>
      <p className="text-sm text-vg2-muted mb-2">
        {view.property_type_label} · {view.owner_display_label}
      </p>
      {view.owner_contact && (view.owner_contact.name || view.owner_contact.email) && (
        <div className="mb-2 p-3 border border-vg2-line rounded bg-vg2-subtle"
             data-testid="pa-case-owner-contact">
          <p className="text-xs uppercase tracking-[0.12em] text-vg2-muted mb-1">
            Recapiti del proprietario (consenso valido)
          </p>
          {view.owner_contact.name && (
            <p className="text-sm text-vg2-ink" data-testid="pa-case-owner-name">
              {view.owner_contact.name}
            </p>
          )}
          {view.owner_contact.email && (
            <p className="text-sm text-vg2-ink" data-testid="pa-case-owner-email">
              {view.owner_contact.email}
            </p>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-3 items-center">
        <span className={`text-xs px-2 py-1 rounded ${
          view.reassessment_required
            ? "bg-vg2-red text-white"
            : "bg-vg2-subtle text-vg2-ink"
        }`}
              data-testid="pa-case-status-chip">
          {STATUS_LABELS_IT[view.pa_status] || view.pa_status}
        </span>
        <span className="text-sm text-vg2-ink"
              data-testid="pa-case-next-action">
          {view.next_professional_action_label}
        </span>
      </div>
    </header>
  );
}


function CallFocusSection({ view, onFocusClick }) {
  const focus = view.handoff_recommended_call_focus || [];
  const paFocus = view.pa_current_focus || [];
  if (focus.length === 0 && paFocus.length === 0) return null;
  return (
    <Section id="focus" title="Punti da affrontare nella call"
             testid="pa-case-focus">
      {focus.length > 0 && (
        <ul className="space-y-2 mb-3"
            data-testid="pa-case-focus-handoff">
          {focus.map((f, idx) => (
            <li key={`hf-${idx}`}>
              <button onClick={() => onFocusClick(f.target)}
                      data-testid={`pa-case-focus-item-${idx}`}
                      className="w-full text-left px-3 py-2 border border-vg2-line rounded hover:border-vg2-red hover:text-vg2-red">
                {f.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      {paFocus.length > 0 && (
        <>
          <p className="text-xs text-vg2-muted mb-2">
            Priorità aggiuntive dal Property Advisor
          </p>
          <ul className="space-y-2" data-testid="pa-case-focus-pa">
            {paFocus.map((f, idx) => (
              <li key={`pf-${idx}`}>
                <button onClick={() => onFocusClick(f.target)}
                        data-testid={`pa-case-focus-pa-item-${idx}`}
                        className="w-full text-left px-3 py-2 border border-dashed border-vg2-line rounded hover:border-vg2-red hover:text-vg2-red">
                  {f.label}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </Section>
  );
}


function ObjectiveSection({ view }) {
  return (
    <Section id="OBJECTIVE" title="Obiettivo del proprietario">
      <p className="text-sm text-vg2-ink mb-1">{view.objective_summary}</p>
      {view.objective_target_outcome && (
        <p className="text-xs text-vg2-muted">
          Risultato atteso: {view.objective_target_outcome}
        </p>
      )}
      {view.objective_timeframe && (
        <p className="text-xs text-vg2-muted">
          Tempi: {view.objective_timeframe}
        </p>
      )}
    </Section>
  );
}


function DecisionSection({ view }) {
  return (
    <Section id="DECISION" title="Decisione">
      <p className="text-sm text-vg2-ink">
        Direzione: <strong>{view.selected_direction_label || "—"}</strong>
      </p>
      <p className="text-sm text-vg2-muted">
        Allineamento SmartEva: {view.decision_smarteva_alignment || "—"}
      </p>
      {view.decision_status && (
        <p className="text-xs text-vg2-muted">
          Stato decisione: {view.decision_status}
        </p>
      )}
    </Section>
  );
}


function ActionPlanSection({ view }) {
  const prereqs = view.prerequisites_overlay || [];
  const docs = view.documents_overlay || [];
  const roles = view.professional_roles_overlay || [];

  return (
    <Section id="ACTION_PLAN" title="Piano d'azione">
      {!view.action_plan_id && (
        <p className="text-sm text-vg2-muted">Nessun piano d'azione corrente.</p>
      )}
      {view.action_plan_id && (
        <>
          {/* --- Analisi caso + stato --- */}
          <div className="mb-4">
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="text-vg2-ink">
                Stato: <strong data-testid="pa-plan-status">{view.action_plan_status}</strong>
              </span>
              {view.action_plan_version && (
                <span className="text-vg2-muted">
                  Versione: <strong>v{view.action_plan_version}</strong>
                </span>
              )}
              {view.action_plan_lifecycle && (
                <span className="text-vg2-muted">
                  Ciclo: <strong>{view.action_plan_lifecycle}</strong>
                </span>
              )}
            </div>
            {view.objective_summary && (
              <p className="text-sm text-vg2-muted mt-3">
                <strong className="text-vg2-ink">Analisi:</strong> {view.objective_summary}
              </p>
            )}
            {view.action_plan_next_action_title && (
              <p className="text-sm text-vg2-muted mt-2">
                <strong className="text-vg2-ink">Prossima azione:</strong>{" "}
                <span data-testid="pa-plan-next-action">
                  {view.action_plan_next_action_title}
                </span>
              </p>
            )}
          </div>

          {/* --- Prerequisiti / prossimi passi (milestones) --- */}
          {prereqs.length > 0 && (
            <div className="mb-5" data-testid="pa-plan-prereqs">
              <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-2">
                Prossimi passi ({prereqs.length})
              </p>
              <ul className="space-y-1.5">
                {prereqs.map((p) => (
                  <li key={p.milestone_id}
                      className="text-sm text-vg2-ink flex items-start gap-2"
                      data-testid={`pa-plan-prereq-${p.milestone_id}`}>
                    <StatusDot status={p.plan_status} />
                    <span className="flex-1">{p.title}</span>
                    <StatusBadge status={p.plan_status} />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* --- Categorie professionisti necessari --- */}
          {roles.length > 0 && (
            <div className="mb-5" data-testid="pa-plan-roles">
              <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-2">
                Categorie professionisti coinvolti ({roles.length})
              </p>
              <ul className="space-y-2">
                {roles.map((r) => (
                  <li key={r.role_key}
                      className="text-sm border-l-2 border-vg2-red/30 pl-3"
                      data-testid={`pa-plan-role-${r.role_key}`}>
                    <p className="font-semibold text-vg2-ink">
                      {ROLE_LABELS_IT[r.role_key] || r.role_key}
                    </p>
                    <p className="text-vg2-muted text-xs mt-0.5">{r.purpose}</p>
                    {r.reason && (
                      <p className="text-vg2-muted text-xs mt-0.5 italic">{r.reason}</p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* --- Documenti richiesti --- */}
          {docs.length > 0 && (
            <div data-testid="pa-plan-docs">
              <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-2">
                Documenti richiesti ({docs.length})
              </p>
              <ul className="space-y-1.5">
                {docs.map((d) => (
                  <li key={d.document_key}
                      className="text-sm text-vg2-ink flex items-start gap-2"
                      data-testid={`pa-plan-doc-${d.document_key}`}>
                    <StatusDot status={d.plan_status} />
                    <span className="flex-1">{d.document_label}</span>
                    <StatusBadge status={d.plan_status} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </Section>
  );
}


// Italian labels for role categories live in the existing ROLE_LABELS_IT
// constant at the bottom of this file (single source of truth for the
// PA workspace). The ActionPlanSection above references it directly.



function StatusDot({ status }) {
  const color = _statusColor(status);
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${color}`}
      aria-hidden
    />
  );
}


function StatusBadge({ status }) {
  const label = _statusLabel(status);
  if (!label) return null;
  return (
    <span className="text-[10px] uppercase tracking-wider text-vg2-muted">
      {label}
    </span>
  );
}


function _statusColor(s) {
  if (!s) return "bg-gray-300";
  const up = String(s).toUpperCase();
  if (up.includes("DONE") || up.includes("COMPLETE") || up === "OK") return "bg-emerald-500";
  if (up.includes("IN_PROGRESS") || up.includes("DOING") || up.includes("ONGOING")) return "bg-amber-500";
  if (up.includes("BLOCKED") || up.includes("STALE") || up.includes("MISSING")) return "bg-red-500";
  return "bg-gray-400";
}


function _statusLabel(s) {
  if (!s) return "";
  const up = String(s).toUpperCase();
  const map = {
    DONE: "fatto", COMPLETED: "fatto", OK: "ok",
    IN_PROGRESS: "in corso", DOING: "in corso", ONGOING: "in corso",
    OPEN: "aperto", TODO: "da fare", PLANNED: "pianificato",
    BLOCKED: "bloccato", STALE: "da rivedere", MISSING: "mancante",
  };
  return map[up] || up.toLowerCase().replace(/_/g, " ");
}


function HandoffSection({ view }) {
  if (!view.handoff_status) return null;
  return (
    <Section id="handoff" title="EVA · Handoff">
      <p className="text-sm text-vg2-ink mb-2">
        Stato handoff: <strong>{view.handoff_status}</strong>
      </p>
      {view.handoff_owner_objective_summary && (
        <p className="text-sm text-vg2-muted mb-2">
          Sintesi obiettivo: {view.handoff_owner_objective_summary}
        </p>
      )}
      {view.handoff_owner_concerns?.length > 0 && (
        <ListBlock title="Preoccupazioni" items={view.handoff_owner_concerns}
                   testid="pa-case-handoff-concerns" />
      )}
      {view.handoff_owner_objections?.length > 0 && (
        <ListBlock title="Obiezioni" items={view.handoff_owner_objections}
                   testid="pa-case-handoff-objections" />
      )}
      {view.handoff_owner_questions?.length > 0 && (
        <ListBlock title="Domande del proprietario"
                   items={view.handoff_owner_questions}
                   testid="pa-case-handoff-questions" />
      )}
      {view.handoff_new_candidate_facts?.length > 0 && (
        <ListBlock title="Nuovi fatti indicati"
                   items={view.handoff_new_candidate_facts}
                   testid="pa-case-handoff-facts" />
      )}
      {view.handoff_what_owner_wants_from_pa && (
        <p className="text-sm text-vg2-ink mt-2">
          Cosa vuole dal PA: {view.handoff_what_owner_wants_from_pa}
        </p>
      )}
    </Section>
  );
}


function ListBlock({ title, items, testid }) {
  return (
    <div className="mt-2" data-testid={testid}>
      <p className="text-xs uppercase tracking-[0.12em] text-vg2-muted mb-1">
        {title}
      </p>
      <ul className="list-disc pl-5 text-sm text-vg2-ink">
        {items.map((t, i) => <li key={i}>{t}</li>)}
      </ul>
    </div>
  );
}


function EntriesSection({ view, onAdd }) {
  return (
    <Section id="NEW_FACTS" title="Fatti ed evidenze del PA">
      {view.entries?.length ? (
        <ul className="divide-y divide-vg2-line border border-vg2-line rounded"
            data-testid="pa-case-entries-list">
          {view.entries.map((e) => (
            <li key={e.entry_id} className="p-3"
                data-testid={`pa-entry-${e.entry_id}`}>
              <p className="text-xs text-vg2-muted mb-1">
                {ENTRY_TYPE_LABELS_IT[e.entry_type] || e.entry_type}
                {e.materiality === "MATERIAL" && (
                  <span className="ml-2 text-vg2-red font-medium">
                    · Materiale
                  </span>
                )}
                {e.verification_status === "VERIFIED" && (
                  <span className="ml-2 text-vg2-red">· Verificato</span>
                )}
              </p>
              <p className="text-sm font-medium">{e.topic}</p>
              <p className="text-sm text-vg2-ink">{e.summary}</p>
              {e.verification_basis && (
                <p className="text-xs text-vg2-muted mt-1">
                  Base: {VERIFICATION_BASIS_LABELS_IT[e.verification_basis]}
                </p>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-vg2-muted">
          Nessuna registrazione dal PA.
        </p>
      )}
      <button onClick={onAdd}
              data-testid="pa-case-add-entry-btn"
              className="mt-3 px-4 py-2 bg-vg2-red text-white rounded hover:opacity-90">
        Aggiungi registrazione
      </button>
    </Section>
  );
}


function DocumentsSection({ view }) {
  if (!view.documents_overlay?.length) return null;
  return (
    <Section id="DOCUMENTS" title="Documenti (stato operativo)">
      <ul className="divide-y divide-vg2-line border border-vg2-line rounded"
          data-testid="pa-case-documents-list">
        {view.documents_overlay.map((d) => (
          <li key={d.document_key}
              className="p-3 flex items-center justify-between"
              data-testid={`pa-doc-${d.document_key}`}>
            <div>
              <p className="text-sm font-medium">{d.document_label}</p>
              <p className="text-xs text-vg2-muted">
                Piano: {d.plan_status}
                {d.operational_state && (
                  <> · Operativo: {DOC_STATE_LABELS_IT[d.operational_state] || d.operational_state}</>
                )}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </Section>
  );
}


function PrerequisitesSection({ view }) {
  if (!view.prerequisites_overlay?.length) return null;
  return (
    <Section id="PREREQUISITES" title="Punti critici (prerequisiti)">
      <ul className="divide-y divide-vg2-line border border-vg2-line rounded"
          data-testid="pa-case-prerequisites-list">
        {view.prerequisites_overlay.map((p) => (
          <li key={p.milestone_id}
              className="p-3"
              data-testid={`pa-prereq-${p.milestone_id}`}>
            <p className="text-sm font-medium">{p.title}</p>
            <p className="text-xs text-vg2-muted">
              Piano: {p.plan_status}
              {p.operational_state && (
                <> · Operativo: {PREREQ_STATE_LABELS_IT[p.operational_state] || p.operational_state}</>
              )}
            </p>
          </li>
        ))}
      </ul>
    </Section>
  );
}


function OpenPointsSection({ view }) {
  const hasHandoffPoints = (view.handoff_open_points || []).length > 0;
  const hasOwnerQuestions = (view.handoff_owner_questions || []).length > 0;
  if (!hasHandoffPoints && !hasOwnerQuestions) return null;
  return (
    <Section id="OPEN_QUESTIONS" title="Punti aperti">
      {hasHandoffPoints && (
        <ul className="list-disc pl-5 text-sm text-vg2-ink"
            data-testid="pa-case-open-points-list">
          {view.handoff_open_points.map((p, i) => (
            <li key={i}>{p.topic}</li>
          ))}
        </ul>
      )}
    </Section>
  );
}


function RolesSection({ view }) {
  if (!view.professional_roles_overlay?.length) return null;
  return (
    <Section id="PROFESSIONAL_ROLES" title="Professionalità necessarie">
      <ul className="divide-y divide-vg2-line border border-vg2-line rounded"
          data-testid="pa-case-roles-list">
        {view.professional_roles_overlay.map((r) => (
          <li key={r.role_key} className="p-3"
              data-testid={`pa-role-${r.role_key}`}>
            <p className="text-sm font-medium">
              {ROLE_LABELS_IT[r.role_key] || r.role_key}
            </p>
            <p className="text-xs text-vg2-muted">
              {r.purpose}
              {r.operational_state && (
                <> · Stato: {ROLE_STATE_LABELS_IT[r.operational_state] || r.operational_state}</>
              )}
            </p>
            {r.reason && (
              <p className="text-xs text-vg2-muted mt-1">
                Motivo: {r.reason}
              </p>
            )}
          </li>
        ))}
      </ul>
    </Section>
  );
}


function CallSummarySection({ view, onRecord }) {
  return (
    <Section id="call_summary" title="Sintesi della call">
      {view.latest_call_summary_id ? (
        <>
          <p className="text-sm text-vg2-ink"
             data-testid="pa-case-latest-call-summary">
            Ultima call: {new Date(view.latest_call_summary_at)
              .toLocaleString("it-IT")}
          </p>
          {view.latest_owner_next_intent && (
            <p className="text-xs text-vg2-muted">
              Intento del proprietario: {OWNER_INTENT_LABELS_IT[view.latest_owner_next_intent]
                || view.latest_owner_next_intent}
            </p>
          )}
        </>
      ) : (
        <p className="text-sm text-vg2-muted">
          Nessuna sintesi di call ancora registrata.
        </p>
      )}
      <button onClick={onRecord}
              data-testid="pa-case-record-call-btn"
              className="mt-3 px-4 py-2 bg-vg2-red text-white rounded hover:opacity-90">
        Registra sintesi call
      </button>
    </Section>
  );
}


function ReadinessSection({ view }) {
  return (
    <Section id="readiness" title="Prossima azione professionale">
      <p className="text-sm text-vg2-ink mb-2"
         data-testid="pa-case-readiness-next">
        {view.next_professional_action_label}
      </p>
      {view.ready_for_execution ? (
        <p className="text-sm text-vg2-red font-medium"
           data-testid="pa-case-ready">
          Caso pronto per esecuzione operativa.
        </p>
      ) : view.ready_for_execution_blockers?.length ? (
        <>
          <p className="text-xs text-vg2-muted mb-1">
            Non ancora pronto per l'esecuzione. Motivi:
          </p>
          <ul className="list-disc pl-5 text-sm text-vg2-ink"
              data-testid="pa-case-ready-blockers">
            {view.ready_for_execution_blockers.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        </>
      ) : null}
    </Section>
  );
}


// ---------- drawers ------------------------------------------------------

function EntryDrawer({ paId, caseId, view, onClose, onSaved }) {
  const [entryType, setEntryType] = React.useState("OWNER_STATED_FACT");
  const [topic, setTopic] = React.useState("");
  const [summary, setSummary] = React.useState("");
  const [basis, setBasis] = React.useState("");
  const [factType, setFactType] = React.useState("");
  const [documentKey, setDocumentKey] = React.useState("");
  const [documentState, setDocumentState] = React.useState("");
  const [prereqId, setPrereqId] = React.useState("");
  const [prereqState, setPrereqState] = React.useState("");
  const [roleKey, setRoleKey] = React.useState("");
  const [roleState, setRoleState] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const submit = async () => {
    setErr(null);
    setBusy(true);
    try {
      const payload = {
        entry_type: entryType,
        topic: topic.trim(), summary: summary.trim(),
        operation_id: newOperationId("entry"),
      };
      if (entryType === "VERIFIED_FACT") payload.verification_basis = basis || null;
      if (factType && (entryType === "VERIFIED_FACT" ||
                       entryType === "OWNER_STATED_FACT" ||
                       entryType === "OPEN_QUESTION"))
        payload.fact_type_hint = factType;
      if (entryType === "DOCUMENT_STATUS_UPDATE") {
        payload.document_key = documentKey;
        payload.document_state = documentState;
      }
      if (entryType === "PREREQUISITE_UPDATE") {
        payload.prerequisite_milestone_id = prereqId;
        payload.prerequisite_state = prereqState;
      }
      if (roleKey && roleState) {
        payload.role_key = roleKey;
        payload.role_state = roleState;
      }
      await postPaEntry({ paId, caseId, payload });
      await onSaved();
    } catch (e) { setErr(e); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end md:items-center justify-center p-2 md:p-4 z-50"
         role="dialog" aria-modal="true"
         data-testid="pa-entry-drawer">
      <div className="bg-white rounded max-w-2xl w-full p-5 max-h-[90vh] overflow-y-auto">
        <p className="font-display font-medium text-lg mb-3">
          Nuova registrazione
        </p>
        <label className="block text-xs uppercase text-vg2-muted mb-1">
          Tipo di registrazione
        </label>
        <select value={entryType} onChange={(e) => setEntryType(e.target.value)}
                data-testid="pa-entry-type"
                className="w-full mb-3 px-3 py-2 border border-vg2-line rounded">
          {Object.entries(ENTRY_TYPE_LABELS_IT).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <input value={topic} onChange={(e) => setTopic(e.target.value)}
               placeholder="Argomento"
               data-testid="pa-entry-topic"
               className="w-full mb-3 px-3 py-2 border border-vg2-line rounded" />
        <textarea value={summary} onChange={(e) => setSummary(e.target.value)}
                  placeholder="Dettaglio"
                  data-testid="pa-entry-summary"
                  rows={4}
                  className="w-full mb-3 px-3 py-2 border border-vg2-line rounded" />

        {entryType === "VERIFIED_FACT" && (
          <>
            <label className="block text-xs uppercase text-vg2-muted mb-1">
              Base di verifica (obbligatoria)
            </label>
            <select value={basis} onChange={(e) => setBasis(e.target.value)}
                    data-testid="pa-entry-basis"
                    className="w-full mb-3 px-3 py-2 border border-vg2-line rounded">
              <option value="">Seleziona…</option>
              {Object.entries(VERIFICATION_BASIS_LABELS_IT).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </>
        )}

        {(entryType === "VERIFIED_FACT" ||
          entryType === "OWNER_STATED_FACT" ||
          entryType === "OPEN_QUESTION") && (
          <>
            <label className="block text-xs uppercase text-vg2-muted mb-1">
              Categoria (facoltativa)
            </label>
            <select value={factType} onChange={(e) => setFactType(e.target.value)}
                    data-testid="pa-entry-fact-type"
                    className="w-full mb-3 px-3 py-2 border border-vg2-line rounded">
              <option value="">—</option>
              {FACT_TYPES.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </>
        )}

        {entryType === "DOCUMENT_STATUS_UPDATE" && (
          <>
            <label className="block text-xs uppercase text-vg2-muted mb-1">
              Documento
            </label>
            <select value={documentKey} onChange={(e) => setDocumentKey(e.target.value)}
                    data-testid="pa-entry-doc-key"
                    className="w-full mb-3 px-3 py-2 border border-vg2-line rounded">
              <option value="">Seleziona…</option>
              {view.documents_overlay?.map((d) => (
                <option key={d.document_key} value={d.document_key}>
                  {d.document_label}
                </option>
              ))}
            </select>
            <select value={documentState} onChange={(e) => setDocumentState(e.target.value)}
                    data-testid="pa-entry-doc-state"
                    className="w-full mb-3 px-3 py-2 border border-vg2-line rounded">
              <option value="">Stato operativo…</option>
              {Object.entries(DOC_STATE_LABELS_IT).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </>
        )}

        {entryType === "PREREQUISITE_UPDATE" && (
          <>
            <select value={prereqId} onChange={(e) => setPrereqId(e.target.value)}
                    data-testid="pa-entry-prereq-id"
                    className="w-full mb-3 px-3 py-2 border border-vg2-line rounded">
              <option value="">Seleziona prerequisito…</option>
              {view.prerequisites_overlay?.map((p) => (
                <option key={p.milestone_id} value={p.milestone_id}>
                  {p.title}
                </option>
              ))}
            </select>
            <select value={prereqState} onChange={(e) => setPrereqState(e.target.value)}
                    data-testid="pa-entry-prereq-state"
                    className="w-full mb-3 px-3 py-2 border border-vg2-line rounded">
              <option value="">Stato operativo…</option>
              {Object.entries(PREREQ_STATE_LABELS_IT).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </>
        )}

        {err && (
          <p className="text-sm text-vg2-red mb-3"
             data-testid="pa-entry-error">
            {err.code === "VERIFICATION_BASIS_REQUIRED"
              ? "Devi indicare una base di verifica per un fatto verificato."
              : (err.message || "Errore imprevisto.")}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
                  data-testid="pa-entry-cancel"
                  className="px-4 py-2 border border-vg2-line rounded hover:bg-vg2-subtle">
            Annulla
          </button>
          <button onClick={submit} disabled={busy || !topic.trim() || !summary.trim()}
                  data-testid="pa-entry-save"
                  className="px-4 py-2 bg-vg2-red text-white rounded disabled:opacity-40">
            {busy ? "…" : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}


function CallSummaryDrawer({ paId, caseId, view, onClose, onSaved }) {
  const [topics, setTopics] = React.useState("");
  const [intent, setIntent] = React.useState("NOT_STATED");
  const [reass, setReass] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const submit = async () => {
    setErr(null); setBusy(true);
    try {
      const payload = {
        topics_covered: topics.split("\n").map((s) => s.trim()).filter(Boolean),
        entry_refs: (view.entries || []).map((e) => e.entry_id),
        facts_added: [], open_questions: [],
        document_updates: [], prerequisite_updates: [],
        reassessment_required: reass,
        reassessment_scope: [],
        owner_next_intent: intent,
        operation_id: newOperationId("call"),
      };
      await postPaCallSummary({ paId, caseId, payload });
      await onSaved();
    } catch (e) { setErr(e); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end md:items-center justify-center p-2 md:p-4 z-50"
         role="dialog" aria-modal="true"
         data-testid="pa-call-drawer">
      <div className="bg-white rounded max-w-2xl w-full p-5 max-h-[90vh] overflow-y-auto">
        <p className="font-display font-medium text-lg mb-3">
          Sintesi call
        </p>
        <label className="block text-xs uppercase text-vg2-muted mb-1">
          Argomenti trattati (uno per riga)
        </label>
        <textarea value={topics} onChange={(e) => setTopics(e.target.value)}
                  rows={4}
                  data-testid="pa-call-topics"
                  className="w-full mb-3 px-3 py-2 border border-vg2-line rounded" />
        <label className="block text-xs uppercase text-vg2-muted mb-1">
          Intento del proprietario
        </label>
        <select value={intent} onChange={(e) => setIntent(e.target.value)}
                data-testid="pa-call-intent"
                className="w-full mb-3 px-3 py-2 border border-vg2-line rounded">
          {Object.entries(OWNER_INTENT_LABELS_IT).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <label className="text-sm flex items-center gap-2 mb-3">
          <input type="checkbox" checked={reass}
                 onChange={(e) => setReass(e.target.checked)}
                 data-testid="pa-call-reass" />
          Serve una riesamina delle informazioni
        </label>
        {err && (
          <p className="text-sm text-vg2-red mb-3"
             data-testid="pa-call-error">
            {err.message || "Errore imprevisto."}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
                  data-testid="pa-call-cancel"
                  className="px-4 py-2 border border-vg2-line rounded hover:bg-vg2-subtle">
            Annulla
          </button>
          <button onClick={submit} disabled={busy}
                  data-testid="pa-call-save"
                  className="px-4 py-2 bg-vg2-red text-white rounded disabled:opacity-40">
            {busy ? "…" : "Salva sintesi"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------- labels -------------------------------------------------------

const ENTRY_TYPE_LABELS_IT = {
  VERIFIED_FACT: "Fatto verificato",
  OWNER_STATED_FACT: "Fatto dichiarato dal proprietario",
  PROFESSIONAL_NOTE: "Nota professionale",
  OPEN_QUESTION: "Domanda aperta",
  DOCUMENT_STATUS_UPDATE: "Aggiornamento documento",
  PREREQUISITE_UPDATE: "Aggiornamento prerequisito",
  DECISION_RECONSIDERATION: "Ripensamento sulla decisione",
};

const VERIFICATION_BASIS_LABELS_IT = {
  DOCUMENT_REVIEW: "Esame di un documento",
  DIRECT_OBSERVATION: "Osservazione diretta",
  PROFESSIONAL_EVIDENCE: "Evidenza professionale",
  OFFICIAL_RECORD: "Fonte ufficiale",
  OTHER_DOCUMENTED_BASIS: "Altra base documentata",
};

const DOC_STATE_LABELS_IT = {
  AVAILABLE: "Disponibile",
  REQUESTED: "Richiesto",
  TO_VERIFY: "Da verificare",
  REVIEWED: "Esaminato",
  CONFIRMED_MISSING: "Mancante (confermato)",
};

const PREREQ_STATE_LABELS_IT = {
  OPEN: "Aperto",
  VERIFYING: "In verifica",
  RESOLVED: "Risolto",
  UNRESOLVED: "Non risolto",
  NOT_APPLICABLE: "Non applicabile",
};

const ROLE_LABELS_IT = {
  PROPERTY_ADVISOR: "Property Advisor",
  REAL_ESTATE_AGENT: "Agente immobiliare",
  ARCHITECT: "Architetto",
  SURVEYOR: "Geometra",
  ENGINEER: "Ingegnere",
  NOTARY: "Notaio",
  LAWYER: "Avvocato",
  TAX_ADVISOR: "Commercialista",
  CONTRACTOR: "Impresa edile",
  ENERGY_CERTIFIER: "Certificatore energetico",
  FINANCIAL_ADVISOR: "Consulente finanziario",
};

const ROLE_STATE_LABELS_IT = {
  POTENTIALLY_NEEDED: "Possibile",
  CONFIRMED_NEEDED: "Necessario",
  NOT_NEEDED: "Non necessario",
  TO_VERIFY: "Da verificare",
};

const OWNER_INTENT_LABELS_IT = {
  PROCEED: "Vuole procedere",
  NEEDS_TIME: "Serve tempo",
  REASSESS: "Vuole riesaminare",
  STOP: "Vuole fermarsi",
  NOT_STATED: "Non dichiarato",
};

const FACT_TYPES = [
  "PROPERTY_IDENTITY", "PROPERTY_SURFACE", "PROPERTY_CONDITION",
  "OCCUPANCY", "LEGAL_DOCUMENT_ISSUE", "TECHNICAL_ISSUE",
  "BUDGET_CONSTRAINT", "TIMING_CONSTRAINT", "OWNER_OBJECTIVE_CHANGE",
  "RENOVATION_FEASIBILITY", "FINANCING_OR_ENCUMBRANCE",
  "SCENARIO_FEASIBILITY", "OTHER",
];
